import { Button, Container, Typography } from "@mui/material";
import { captureSentryException } from "@utils";
import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  componentName?: string;
  userId?: string;
  deviceId?: string;
  orgId?: string;
  sessionId?: string;
  userAgent?: string;
  viewport?: { width: number; height: number };
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Capture error with Sentry including comprehensive context
    captureSentryException(error, {
      errorInfo,
      componentName: this.props.componentName || "ErrorBoundary",
      errorBoundary: "GenericErrorBoundary",
      timestamp: new Date().toISOString(),
      userAgent: this.props.userAgent || navigator.userAgent,
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      referrer: document.referrer,
      context: {
        userId: this.props.userId,
        deviceId: this.props.deviceId,
        orgId: this.props.orgId,
        sessionId: this.props.sessionId || this.generateSessionId(),
        viewport: this.props.viewport || {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        screenResolution: {
          width: screen.width,
          height: screen.height,
        },
      },
    });

    this.props.onError?.(error, errorInfo);
  }

  generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Container
          maxWidth="md"
          className="items-center justify-center flex flex-auto flex-col h-full min-h-[300px] p-4"
        >
          <Typography variant="h5" className="mb-4 text-center">
            Something went wrong
            {this.props.componentName && ` in ${this.props.componentName}`}
          </Typography>
          <Typography
            variant="body1"
            className="mb-4 text-center text-gray-600"
          >
            An unexpected error occurred. Please try reloading the page.
          </Typography>
          {process.env.NODE_ENV === "development" && this.state.error && (
            <Typography
              variant="body2"
              className="mb-4 text-center text-red-600 bg-red-50 p-2 rounded"
            >
              Error: {this.state.error.message}
            </Typography>
          )}
          <Button
            className="mt-4"
            variant="contained"
            onClick={this.handleReload}
          >
            Reload Page
          </Button>
        </Container>
      );
    }

    return this.props.children;
  }
}
